// The money on one sale, for the sale popup (components/SaleMoney.tsx):
// the live sale, its payments / broker payments / advances used / credit
// notes, and the per-broker commission rows.

import { useEffect, useState } from 'react';
import { collection, doc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import type { SaleInvoice, SalePayment, BrokerPayment, CustomerAdvance, AdvanceApplication, SalesReturn } from '../types';

const round2 = (n: number) => Math.round(n * 100) / 100;

export function fmtDay(dateStr?: string) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function saleOutstanding(inv: SaleInvoice): number {
  return Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0));
}

/** One row per broker on a sale: the legacy header broker, then each per-line Broker 1 / 2 (on the full amount). */
export interface SaleBrokerRow {
  brokerEntityId: string;
  /** true = a per-line broker (its payments carry brokerEntityId). */
  line: boolean;
  percent: number | null;
  commission: number;
}
export function saleBrokerRows(inv: SaleInvoice): SaleBrokerRow[] {
  const rows: SaleBrokerRow[] = [];
  if (inv.brokerEntityId && (inv.brokerCommissionUSD ?? 0) > 0.005) {
    rows.push({ brokerEntityId: inv.brokerEntityId, line: false, percent: inv.brokerCommissionPercent ?? null, commission: inv.brokerCommissionUSD ?? 0 });
  }
  const totals = new Map<string, number>();
  const pcts = new Map<string, Set<number>>();
  const add = (id: string | undefined, pct: number | undefined, base: number) => {
    if (!id || !pct) return;
    totals.set(id, round2((totals.get(id) ?? 0) + round2(base * pct / 100)));
    const s = pcts.get(id) ?? new Set<number>(); s.add(pct); pcts.set(id, s);
  };
  for (const li of inv.lineItems ?? []) {
    const base = li.grossAmountUSD ?? li.amountUSD ?? 0;
    add(li.broker1EntityId, li.broker1CommissionPercent, base);
    add(li.broker2EntityId, li.broker2CommissionPercent, base);
  }
  for (const [id, amt] of totals) {
    const s = pcts.get(id);
    rows.push({ brokerEntityId: id, line: true, percent: s && s.size === 1 ? [...s][0] : null, commission: amt });
  }
  return rows;
}

// ── Data ──────────────────────────────────────────────────────────────────

/** The sale as it is now (live), falling back to the copy the page opened. */
export function useLiveSale(opened: SaleInvoice | null): SaleInvoice | null {
  const [live, setLive] = useState<SaleInvoice | null>(null);
  const id = opened?.id;
  useEffect(() => {
    const db = getClientDb();
    if (!db || !id) return;
    return onSnapshot(doc(db, 'sales', id), snap => {
      if (snap.exists()) setLive({ id: snap.id, ...snap.data() } as SaleInvoice);
    }, () => {});
  }, [id]);
  if (!opened) return null;
  return live && live.id === opened.id ? live : opened;
}

export interface SaleMoneyData {
  payments: SalePayment[];
  brokerPayments: BrokerPayment[];
  advanceUses: (AdvanceApplication & { advanceNo: string })[];
  credits: SalesReturn[];
  /** Small differences closed with "Settle difference" (sales/{id}/adjustments, kind 'settle'). */
  settles: SaleSettle[];
  loading: boolean;
}
export interface SaleSettle { id: string; kind: 'settle'; date: string; amountUSD: number; dueGroupId?: string | null; createdBy?: string }

/** The most "Settle difference" may close: 1% of the invoice or 50, whichever is lower. */
export function settleLimit(inv: Pick<SaleInvoice, 'totalAmountUSD'>): number {
  return Math.min(50, Math.round(inv.totalAmountUSD * 0.01 * 100) / 100);
}

export function useSaleMoney(invoice: SaleInvoice | null): SaleMoneyData {
  const [payments, setPayments] = useState<SalePayment[]>([]);
  const [brokerPayments, setBrokerPayments] = useState<BrokerPayment[]>([]);
  const [credits, setCredits] = useState<SalesReturn[]>([]);
  const [settles, setSettles] = useState<SaleSettle[]>([]);
  const [advances, setAdvances] = useState<CustomerAdvance[]>([]);
  const [advanceUses, setAdvanceUses] = useState<(AdvanceApplication & { advanceNo: string })[]>([]);
  const [loaded, setLoaded] = useState<string>('');

  const id = invoice?.id;
  const customer = invoice?.toEntityId;

  useEffect(() => {
    const db = getClientDb();
    if (!db || !id) return;
    const u1 = onSnapshot(collection(db, 'sales', id, 'payments'), snap => {
      setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() } as SalePayment)));
      setLoaded(id);
    }, () => setLoaded(id));
    const u2 = onSnapshot(collection(db, 'sales', id, 'brokerPayments'),
      snap => setBrokerPayments(snap.docs.map(d => ({ id: d.id, ...d.data() } as BrokerPayment))), () => {});
    const u3 = onSnapshot(query(collection(db, 'salesReturns'), where('saleId', '==', id)),
      snap => setCredits(snap.docs.map(d => ({ id: d.id, ...d.data() } as SalesReturn))), () => {});
    const u4 = onSnapshot(collection(db, 'sales', id, 'adjustments'),
      snap => setSettles(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleSettle)).filter(x => x.kind === 'settle')), () => {});
    return () => { u1(); u2(); u3(); u4(); };
  }, [id]);

  // Advances live under the customer; read their applications for this sale.
  useEffect(() => {
    const db = getClientDb();
    if (!db || !customer) return;
    return onSnapshot(query(collection(db, 'customerAdvances'), where('toEntityId', '==', customer)),
      snap => setAdvances(snap.docs.map(d => ({ id: d.id, ...d.data() } as CustomerAdvance))), () => {});
  }, [customer]);

  const advKey = advances.map(a => `${a.id}:${a.appliedUSD}`).join('|');
  useEffect(() => {
    const db = getClientDb();
    if (!db || !id) return;
    let cancelled = false;
    const used = advances.filter(a => (a.appliedUSD ?? 0) > 0.005);
    Promise.all(used.map(async a => {
      const snap = await getDocs(collection(db, 'customerAdvances', a.id, 'applications'));
      return snap.docs
        .map(d => ({ id: d.id, ...d.data() } as AdvanceApplication))
        .filter(app => app.invoiceId === id)
        .map(app => ({ ...app, advanceNo: a.advanceNo }));
    })).then(lists => { if (!cancelled) setAdvanceUses(lists.flat()); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, advKey]);

  const fresh = loaded === id;
  return {
    payments: fresh ? payments : [],
    brokerPayments: fresh ? brokerPayments : [],
    advanceUses: fresh ? advanceUses : [],
    credits: fresh ? credits : [],
    settles: fresh ? settles : [],
    loading: !fresh,
  };
}
